import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import * as Pet from '../pet';
import { Locale } from '../ruleset/locale';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { Feature } from '../analytics';
import { isOnlineOrIdle } from '../getters/presence';

const schema = {
  sendCardReceivedOA: SB.object({ cardId: SB.string() }),
  sendCardRequestOA: SB.object({
    cardId: SB.string(),
    requesterId: SB.string(),
    requestedAt: SB.int().optional(),
  }),
};

const actions = createScheduledActions(schema)({
  sendCardReceivedOA: async (state, { cardId }, api) => {
    // Player is online or idle. Bail.
    if (isOnlineOrIdle(api)) return;

    // Notifications are off. Bail.
    if (state.settings.receiveMessages === 'off') return;

    // Get data for chatbot message.
    const { getCurrentEvolution } = Pet.getters;
    const species = getCurrentEvolution(state)?.species ?? 'ball';
    const petName = state.pet.name;

    const payload = generateReplicantChatbotPayload({
      feature: Feature.CARDS._,
      $subFeature: Feature.CARDS.RECEIVED,
    });
    payload.$deepLink = { type: 'openCardCollection' };

    // Render the chatbot message.
    const message = getChatbotFlexMessage({
      template: 'cardReceived',
      payload,
      data: {
        locale: state.settings.locale as Locale,
        petName,
        species,
        cardId,
      },
    });

    // Deliver the chatbot message forcing OA.
    deliverChatbotMessage(state, api, {
      receiverId: state.id,
      message,
      channel: 'oa',
    });
  },

  sendCardRequestOA: async (
    state,
    { cardId, requesterId, requestedAt },
    api,
  ) => {
    // Player is online or idle. Bail.
    if (isOnlineOrIdle(api)) return;

    // Notifications are off. Bail.
    if (state.settings.receiveMessages === 'off') return;

    // Get data for chatbot message.
    const { getCurrentEvolution } = Pet.getters;
    const species = getCurrentEvolution(state)?.species ?? 'ball';
    const petName = state.pet.name;
    const now = api.date.now();

    const payload = {
      ...generateReplicantChatbotPayload({
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      }),
      // Include the same entry fields the card share launch handler expects.
      // The kind selects the request flow, while the method attributes OA delivery.
      // `playerID` carries the requester id for direct OA request messages.
      cardId,
      subFeature: Feature.CARDS.REQUEST,
      cardShareKind: 'request',
      cardShareMethod: 'request',
      playerID: requesterId,
      cardShareCreatedAt: requestedAt ?? now,
    };

    // Render the chatbot message.
    const message = getChatbotFlexMessage({
      template: 'cardRequested',
      payload,
      data: {
        locale: state.settings.locale as Locale,
        petName,
        species,
        cardId,
      },
    });

    // Deliver the chatbot message forcing OA.
    deliverChatbotMessage(state, api, {
      receiverId: state.id,
      message,
      channel: 'oa',
    });
  },
});

export const cardsScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
